import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  RefreshCw,
  FileText,
  Clock,
  Truck,
  PackageCheck,
  X,
  Upload,
  ThumbsUp,
  ThumbsDown,
  Trash2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import StatusPipeline from "@/components/StatusPipeline";
import MessageThread from "@/components/MessageThread";
import MonthlyStats from "@/components/MonthlyStats";
import EmailAuditPanel from "@/components/EmailAuditPanel";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["OPEN", "IN PROGRESS", "DELIVERY", "CLOSED"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [closureReason, setClosureReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [uploadingVehiclePhoto, setUploadingVehiclePhoto] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s, r] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/stats"),
        api.get(
          "/admin/requests" + (filter !== "ALL" ? `?status=${encodeURIComponent(filter)}` : "")
        ),
      ]);
      setMe(m.data);
      setStats(s.data);
      setRequests(r.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin/login");
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // noop
    }
    localStorage.removeItem("gec_token");
    navigate("/");
  };

  const openDetail = async (rid) => {
    try {
      const { data } = await api.get(`/admin/requests/${rid}`);
      setSelected(data);
      setNewStatus(data.status);
      setClosureReason(data.closure_reason || "");
      setAdminNotes(data.admin_notes || "");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reloadSelected = async () => {
    if (!selected) return;
    try {
      const { data } = await api.get(`/admin/requests/${selected.request_id}`);
      setSelected(data);
    } catch (err) {
      // noop
    }
  };

  const saveStatus = async () => {
    if (!selected) return;
    if (newStatus === "CLOSED" && !closureReason.trim()) {
      toast.error("Provide a closure reason");
      return;
    }
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/admin/requests/${selected.request_id}`, {
        status: newStatus,
        closure_reason: newStatus === "CLOSED" ? closureReason : null,
        admin_notes: adminNotes || null,
      });
      setSelected(data);
      toast.success("Status updated");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingStatus(false);
    }
  };

  const uploadVehiclePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selected) return;
    setUploadingVehiclePhoto(true);
    try {
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        paths.push(data.path);
      }
      const { data } = await api.post(
        `/admin/requests/${selected.request_id}/vehicle-photos`,
        { paths }
      );
      setSelected(data);
      toast.success(`${paths.length} photo(s) sent to customer for approval`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingVehiclePhoto(false);
      e.target.value = "";
    }
  };

  const removeVehiclePhoto = async (path) => {
    if (!selected) return;
    try {
      const { data } = await api.delete(
        `/admin/requests/${selected.request_id}/vehicle-photos`,
        { params: { path } }
      );
      setSelected(data);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const statCard = (label, value, Icon, color) => (
    <div className="bg-[#18181E] border border-white/10 rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          {label}
        </div>
        <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display text-3xl font-extrabold mt-3">{value ?? 0}</div>
    </div>
  );

  if (loading && !me) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-slate-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
              / Admin Dashboard
            </div>
            <h1 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">
              Request <span className="gold-text">pipeline</span>
            </h1>
            {me && (
              <p className="text-sm text-slate-400 mt-2">
                Signed in as <span className="text-amber-400">{me.email}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={refresh}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-4 py-2.5 rounded-xl border border-white/10 text-sm transition"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-300 px-4 py-2.5 rounded-xl border border-red-500/30 text-sm transition"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {statCard("Open", stats?.OPEN, FileText, "bg-amber-500/15 text-amber-400 border border-amber-500/30")}
          {statCard("In Progress", stats?.["IN PROGRESS"], Clock, "bg-blue-500/15 text-blue-400 border border-blue-500/30")}
          {statCard("Delivery", stats?.DELIVERY, Truck, "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30")}
          {statCard("Closed", stats?.CLOSED, PackageCheck, "bg-zinc-500/15 text-zinc-400 border border-zinc-500/30")}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {["ALL", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s.toLowerCase().replace(" ", "-")}`}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-semibold transition ${
                filter === s
                  ? "bg-amber-500 text-black border border-amber-500"
                  : "bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="mb-8">
          <MonthlyStats />
        </div>

        <div className="mb-8">
          <EmailAuditPanel />
        </div>

        <div className="bg-[#18181E] border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-requests-table">
              <thead>
                <tr className="bg-[#121216] text-left text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-4 font-semibold">Request ID</th>
                  <th className="px-5 py-4 font-semibold">Customer</th>
                  <th className="px-5 py-4 font-semibold">Vehicle</th>
                  <th className="px-5 py-4 font-semibold">Budget</th>
                  <th className="px-5 py-4 font-semibold">ZIP</th>
                  <th className="px-5 py-4 font-semibold">Status</th>
                  <th className="px-5 py-4 font-semibold">Approval</th>
                  <th className="px-5 py-4 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center text-slate-500">
                      {loading ? "Loading..." : "No requests yet."}
                    </td>
                  </tr>
                )}
                {requests.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => openDetail(r.request_id)}
                    data-testid={`request-row-${r.request_id}`}
                    className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition"
                  >
                    <td className="px-5 py-4 font-mono-brand text-amber-400 font-semibold">
                      {r.request_id}
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-semibold text-white">{r.customer_name}</div>
                      <div className="text-xs text-slate-500">{r.customer_email}</div>
                    </td>
                    <td className="px-5 py-4">
                      {r.year} {r.make} {r.model}
                      {r.exterior_color && (
                        <div className="text-xs text-slate-500">{r.exterior_color}</div>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {r.budget ? `$${Number(r.budget).toLocaleString()}` : "—"}
                    </td>
                    <td className="px-5 py-4">{r.delivery_zip}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${
                          r.status === "OPEN"
                            ? "bg-amber-500/10 border-amber-500/30 text-amber-400"
                            : r.status === "IN PROGRESS"
                            ? "bg-blue-500/10 border-blue-500/30 text-blue-400"
                            : r.status === "DELIVERY"
                            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                            : "bg-zinc-500/10 border-zinc-500/30 text-zinc-400"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {r.customer_approval ? (
                        r.customer_approval.approved ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold">
                            <ThumbsUp className="w-3.5 h-3.5" /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-400 text-xs font-semibold">
                            <ThumbsDown className="w-3.5 h-3.5" /> No
                          </span>
                        )
                      ) : r.found_vehicle_photos?.length > 0 ? (
                        <span className="text-xs text-amber-400">Pending</span>
                      ) : (
                        <span className="text-xs text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-400 text-xs">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
          data-testid="request-detail-overlay"
        >
          <div
            className="w-full max-w-2xl bg-[#0A0A0C] border-l border-white/10 h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#0A0A0C]/95 backdrop-blur-xl border-b border-white/10 p-5 flex items-center justify-between z-10">
              <div>
                <div className="font-mono-brand text-[10px] uppercase tracking-widest text-amber-400">
                  / Request Detail
                </div>
                <div className="font-display text-xl font-extrabold uppercase mt-1">
                  {selected.request_id}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="close-detail-btn"
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              <StatusPipeline current={selected.status} />

              {/* Found Vehicle Photos (admin upload) */}
              <div className="bg-[#18181E] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-display text-sm font-bold uppercase tracking-wider text-amber-400">
                      Found Vehicle Photos
                    </h4>
                    <p className="text-xs text-slate-500 mt-1">
                      Share the sourced vehicle. Customer will approve or decline.
                    </p>
                  </div>
                  {selected.customer_approval && (
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${
                        selected.customer_approval.approved
                          ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                          : "bg-red-500/10 border-red-500/30 text-red-300"
                      }`}
                      data-testid="admin-approval-badge"
                    >
                      {selected.customer_approval.approved ? (
                        <ThumbsUp className="w-3 h-3" />
                      ) : (
                        <ThumbsDown className="w-3 h-3" />
                      )}
                      {selected.customer_approval.approved ? "Approved" : "Declined"}
                    </span>
                  )}
                </div>

                {selected.found_vehicle_photos?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {selected.found_vehicle_photos.map((p, i) => (
                      <div
                        key={p}
                        className="relative aspect-square rounded-lg overflow-hidden border border-white/10 group"
                        data-testid={`admin-vehicle-photo-${i}`}
                      >
                        <img
                          src={`${BACKEND_URL}/api/files/${p}`}
                          alt={`vehicle-${i}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => removeVehiclePhoto(p)}
                          data-testid={`remove-vehicle-photo-${i}`}
                          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <label
                  className="flex items-center justify-center gap-2 border-2 border-dashed border-white/15 hover:border-amber-500/50 bg-[#121216]/50 rounded-xl p-4 cursor-pointer transition text-sm text-slate-300"
                  data-testid="admin-upload-vehicle-photo-dropzone"
                >
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={uploadVehiclePhotos}
                    data-testid="admin-upload-vehicle-photo-input"
                  />
                  {uploadingVehiclePhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                  ) : (
                    <Upload className="w-4 h-4 text-amber-400" />
                  )}
                  <span className="font-semibold uppercase tracking-wider text-xs">
                    {uploadingVehiclePhoto ? "Uploading..." : "Upload Vehicle Photos"}
                  </span>
                </label>

                {selected.customer_approval?.note && (
                  <div className="mt-4 bg-[#0A0A0C] border border-white/10 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                      Customer note
                    </div>
                    <p className="text-sm text-slate-200 mt-1">
                      {selected.customer_approval.note}
                    </p>
                  </div>
                )}
              </div>

              {/* Messages */}
              <MessageThread requestId={selected.request_id} isAdmin />

              {/* Update status */}
              <div className="bg-[#18181E] border border-white/10 rounded-2xl p-5">
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-amber-400 mb-4">
                  Update Status
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setNewStatus(s)}
                      data-testid={`set-status-${s.toLowerCase().replace(" ", "-")}`}
                      className={`px-3 py-2 rounded-lg text-[11px] uppercase tracking-widest font-semibold transition ${
                        newStatus === s
                          ? "bg-amber-500 text-black"
                          : "bg-white/5 text-slate-300 hover:bg-white/10"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                {newStatus === "CLOSED" && (
                  <div className="mb-3">
                    <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2 block">
                      Closure Reason *
                    </label>
                    <select
                      value={closureReason}
                      onChange={(e) => setClosureReason(e.target.value)}
                      data-testid="closure-reason-select"
                      className="w-full bg-[#0A0A0C] border border-white/10 focus:border-amber-500 text-slate-100 rounded-xl px-3 py-2.5 text-sm outline-none"
                    >
                      <option value="">Select a reason...</option>
                      <option value="Vehicle delivered to customer.">
                        Vehicle delivered to customer.
                      </option>
                      <option value="Vehicle exceeded customer's maximum budget.">
                        Vehicle exceeded customer&apos;s maximum budget.
                      </option>
                      <option value="No matching vehicle found at auction.">
                        No matching vehicle found at auction.
                      </option>
                      <option value="Customer withdrew the request.">
                        Customer withdrew the request.
                      </option>
                      <option value="Other">Other (specify below)</option>
                    </select>
                    {closureReason === "Other" && (
                      <input
                        onChange={(e) => setClosureReason(e.target.value)}
                        placeholder="Specify reason"
                        data-testid="closure-reason-custom"
                        className="mt-2 w-full bg-[#0A0A0C] border border-white/10 focus:border-amber-500 text-slate-100 rounded-xl px-3 py-2.5 text-sm outline-none"
                      />
                    )}
                  </div>
                )}

                <div className="mb-3">
                  <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2 block">
                    Admin Notes
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    data-testid="admin-notes-input"
                    placeholder="Internal notes..."
                    className="w-full bg-[#0A0A0C] border border-white/10 focus:border-amber-500 text-slate-100 rounded-xl px-3 py-2.5 text-sm outline-none min-h-[80px]"
                  />
                </div>

                <button
                  onClick={saveStatus}
                  disabled={savingStatus}
                  data-testid="save-status-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-extrabold uppercase tracking-wider px-6 py-3 rounded-xl transition"
                >
                  {savingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
                </button>
              </div>

              {/* Vehicle details */}
              <div className="bg-[#18181E] border border-white/10 rounded-2xl p-5">
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-amber-400 mb-4">
                  Vehicle Requested
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <KV k="Make" v={selected.make} />
                  <KV k="Model" v={selected.model} />
                  <KV k="Year" v={selected.year} />
                  <KV
                    k="Max Mileage"
                    v={selected.max_mileage ? `${selected.max_mileage.toLocaleString()} mi` : "—"}
                  />
                  <KV k="Exterior" v={selected.exterior_color || "—"} />
                  <KV k="Interior" v={selected.interior_color || "—"} />
                  <KV
                    k="Budget"
                    v={selected.budget ? `$${Number(selected.budget).toLocaleString()}` : "—"}
                  />
                  <KV k="Title" v={selected.title_type} />
                  <KV k="Delivery ZIP" v={selected.delivery_zip} />
                </div>
                {selected.desired_features && (
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                      Desired Features
                    </div>
                    <p className="text-sm text-slate-300 mt-1">{selected.desired_features}</p>
                  </div>
                )}
                {selected.additional_notes && (
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                      Notes
                    </div>
                    <p className="text-sm text-slate-300 mt-1">{selected.additional_notes}</p>
                  </div>
                )}
              </div>

              {/* Contact */}
              <div className="bg-[#18181E] border border-white/10 rounded-2xl p-5">
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-amber-400 mb-4">
                  Customer
                </h4>
                <div className="space-y-2 text-sm">
                  <KV k="Name" v={selected.customer_name} />
                  <KV k="Email" v={selected.customer_email} />
                  <KV k="Phone" v={selected.customer_phone || "—"} />
                </div>
              </div>

              {/* Customer reference photos */}
              {selected.photo_paths?.length > 0 && (
                <div className="bg-[#18181E] border border-white/10 rounded-2xl p-5">
                  <h4 className="font-display text-sm font-bold uppercase tracking-wider text-amber-400 mb-4">
                    Customer Reference Photos
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.photo_paths.map((p, i) => (
                      <div
                        key={p}
                        className="aspect-square rounded-lg overflow-hidden border border-white/10"
                      >
                        <img
                          src={`${BACKEND_URL}/api/files/${p}`}
                          alt={`ref-${i}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={reloadSelected}
                data-testid="reload-detail-btn"
                className="w-full text-xs text-slate-400 hover:text-amber-400 py-2"
              >
                Reload details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const KV = ({ k, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
      {k}
    </div>
    <div className="text-slate-200 font-medium mt-0.5">{v}</div>
  </div>
);

export default AdminDashboard;
